import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';
import { Dispatch } from 'redux';
import {
  AddShiftNoteRequest,
  DeleteShiftNoteRequest,
  ShiftNote,
  ShiftNotesRequestBody,
  ShiftNoteState,
  UpdateShiftNoteRequest,
  CompletedShift,
  CompletedShiftsRequestBody
} from '../../../types/ndisSection/shiftNotesTypes';
import dayjs from 'dayjs';

const initialState: ShiftNoteState = {
  shiftNotes: [],
  completedShifts: [],
  loading: false,
  error: null
};

const mapShiftNotesData = (data: any[], reqBody: any): ShiftNote[] => {
  return data.map(
    (item: any): ShiftNote => ({
      id: item.id,
      group_id: reqBody.group_id,
      service_name_id: reqBody.service_name_id,
      component_parent_id: reqBody.component_parent_id,
      nested_component_id: reqBody.nested_component_id,
      user_whoRequested_id: reqBody.user_whoRequested_id,
      shift_id: item.id,
      worker_name: item.workerName || item.worker_name,
      shift_date: item.shiftDate || item.shift_date,
      shift_time: item.shiftTime || item.shift_time,
      shift_details: item.shiftDetails || item.shift_details,
      status: item.status || 'New',
      shift_date_filter: reqBody.shift_date || '',
      clientName: item.clientName,
      title: item.title,
      caseNoteDate: item.caseNoteDate,
      caseNoteTime: item.caseNoteTime,
      assignToStaff: item.assignToStaff || [],
      medicationReceived: item.medicationReceived,
      foodProvided: item.foodProvided,
      personalCare: item.personalCare,
      foodIntakeChartCompleted: item.foodIntakeChartCompleted,
      participantMood: item.participantMood,
      goalsMet: item.goalsMet,
      caseNoteDetails: item.caseNoteDetails,
      timeTaken: item.timeTaken,
      rating: item.rating,
      securityLevel: item.securityLevel,
      shiftStartTime: item.shiftStartTime,
      shiftEndTime: item.shiftEndTime,
      creation_date_time: item.creation_date_time || '',
      supportItemNumber: item.support_item_number || '',
      createdBy: item.created_by || ''
    })
  );
};

const mapCompletedShiftsData = (data: any[]): CompletedShift[] => {
  return data
    .filter((item) => item.actual_end !== null)
    .map(
      (item: any): CompletedShift => ({
        id: item.id,
        shiftStartTime: dayjs(item.actual_start).format('hh:mm A'),
        shiftEndTime: item.actual_end ? dayjs(item.actual_end).format('hh:mm A') : 'N/A',
        totalTime: `${parseFloat(item.actual_worked_hours).toFixed(2)} hours`,
        status: 'Completed',
        user: item.support_worder,
        shiftDate: dayjs(item.actual_start).format('YYYY-MM-DD'),
        title: item.title,
        description: item.description,
        manager: item.manager,
        supportItemNumber: item.support_item_number,
        createdBy: item.created_by
      })
    );
};

const slice = createSlice({
  name: 'shiftNotes',
  initialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },
    hasError(state, action) {
      state.error = action.payload;
    },
    getShiftNotesSuccess(state, action) {
      state.shiftNotes = action.payload;
    },
    addShiftNoteSuccess(state, action) {
      state.shiftNotes.push(action.payload);
    },
    updateShiftNoteSuccess(state, action) {
      const index = state.shiftNotes.findIndex((note) => note.shift_id === action.payload.shift_id);
      if (index !== -1) {
        state.shiftNotes[index] = action.payload;
      }
    },
    removeShiftNoteSuccess(state, action) {
      state.shiftNotes = state.shiftNotes.filter((note) => note.shift_id !== action.payload);
    },
    getCompletedShiftsSuccess(state, action) {
      state.completedShifts = action.payload;
    }
  }
});

export default slice.reducer;

export function fetchShiftNotes(reqBody: ShiftNotesRequestBody) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.setLoading(true));
    try {
      const { data } = await axiosInstance.post('/shift-notes', reqBody);
      if (data) {
        const mappedShiftNotes = mapShiftNotesData(data, reqBody);
        dispatch(slice.actions.getShiftNotesSuccess(mappedShiftNotes));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.setLoading(false));
    }
  };
}

export function addShiftNote(note: AddShiftNoteRequest) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.setLoading(true));
    try {
      const response = await axiosInstance.post('/shiftnote/add', note);
      if (response.data) {
        const mappedShiftNote = mapShiftNotesData([response.data], note)[0];
        dispatch(slice.actions.addShiftNoteSuccess(mappedShiftNote));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.setLoading(false));
    }
  };
}

export function updateShiftNote(note: UpdateShiftNoteRequest) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.setLoading(true));
    try {
      const response = await axiosInstance.post('/shift-notes/update', note);
      if (response.data) {
        const mappedShiftNotes = mapShiftNotesData([response.data], note);
        dispatch(slice.actions.updateShiftNoteSuccess(mappedShiftNotes[0]));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.setLoading(false));
    }
  };
}

export function removeShiftNote(reqBody: DeleteShiftNoteRequest) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.setLoading(true));
    try {
      const response = await axiosInstance.post('/shiftnote/delete', reqBody);
      if (response.data) {
        dispatch(slice.actions.removeShiftNoteSuccess(reqBody.event_id));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.setLoading(false));
    }
  };
}

export function fetchCompletedShifts(reqBody: CompletedShiftsRequestBody) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.setLoading(true));
    try {
      const { data } = await axiosInstance.post('/completed_shifts/fetch', reqBody);
      if (data && data.events) {
        const mappedCompletedShifts = mapCompletedShiftsData(data.events);
        dispatch(slice.actions.getCompletedShiftsSuccess(mappedCompletedShifts));
      } else {
        dispatch(slice.actions.getCompletedShiftsSuccess([]));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.setLoading(false));
    }
  };
}
